// The FixCostEventWriter port - D88 full-cost attribution (0021_full_cost_attribution.sql;
// docs/nightcrew/15-unit-economics-and-cost-attribution.md section 4).
//
// ADMIN-ONLY, and more strictly than run_ledger. `fix_cost_events` has RLS ENABLED + FORCED and the
// app role holds NO grant at all (migrations/0021_full_cost_attribution.sql:189-197). D27 is
// enforced by role privilege rather than by screen discipline: the customer's role cannot reach a
// cost figure. Nothing in this file may be re-exported through a tenant-facing shape.
//
// WHY A SECOND PORT RATHER THAN A WIDER RunLedgerWriter. 0021's header is explicit
// (0021_full_cost_attribution.sql:32-35): `run_ledger` is NOT dropped, it keeps its contract and its
// RLS and stays the model-gateway-local record; the gateway's ledger write becomes the FIRST
// component writer into `fix_cost_events`. Two writes, one truth. Widening `NewRunLedgerEntry`
// instead would drag `work_id`/`work_kind`/`tier`/rate-card freezing into a row shape that four
// other component writers (runner, egress, CI minutes, preview env) do not share.
//
// SEAM RULE. Seam packages never import @nightcrew/db (docs/architecture/contracts.md row 8, and
// the `nightcrew/seam-no-db-import` eslint rule at eslint.config.mjs:53-92). The gateway writes
// through this port; apps/api is the sole binder, exactly as it is for RunLedgerWriter.
import { z } from "zod";
/**
 * The work item a cost belongs to. `work_id`, NOT `fix_id`: features, incidents, Validate journeys
 * and internal passes all cost money, and keying on a fix makes four of the five invisible
 * (0021_full_cost_attribution.sql:19-21).
 */
export const WorkId = z.uuid().brand();
/** A persisted `fix_cost_events` row id (`bigint GENERATED ALWAYS AS IDENTITY`, 0021:90). */
export const FixCostEventId = z.int().positive().brand();
/** Mirrors the `work_kind` pg enum (0021:50; packages/db/src/schema/enums.ts:478). */
export const WorkKind = z.enum(["fix", "feature", "incident", "journey", "internal"]);
/**
 * Mirrors the `work_stage` pg enum (0021:51; packages/db/src/schema/enums.ts:481-489).
 *
 * DELIBERATELY NOT `LedgerStage`. `LedgerStage` is `triage | research | implement | test | review`
 * (model/enums.ts:110); `work_stage` is `triage | research | implement | test | gate | release |
 * retro`. `review` exists only in the first set and `gate`/`release`/`retro` only in the second, so
 * no total mapping exists in either direction and inventing one would put a fabricated stage on a
 * cost row. A caller that meters both tables supplies both stages.
 */
export const WorkStage = z.enum([
    "triage",
    "research",
    "implement",
    "test",
    "gate",
    "release",
    "retro",
]);
/** Mirrors the `cost_component` pg enum (0021:52; packages/db/src/schema/enums.ts:493-507). */
export const CostComponent = z.enum([
    "model_tokens",
    "runner",
    "egress",
    "object_storage",
    "external_api",
    "vector_search",
    "graph_query",
    "embeddings",
    "postgres",
    "ci_minutes",
    "preview_env",
    "control_plane",
    "vendor_flat_rate",
]);
/**
 * 1 direct-metered, 2 attributed-derived, 3 allocated-overhead. The tier is NOT free: 0021's
 * `fix_cost_events_tier_matches_component` CHECK (0021:113-117) pins each component to exactly one
 * tier, because a tier chosen at write time is how an allocation ends up in an actionable headline.
 */
export const CostTier = z.literal([1, 2, 3]);
/**
 * Mirrors the `cost_attribution` pg enum (0021:54; enums.ts:522-528). Only `customer` debits the
 * allowance. There is no default here on purpose: an implicit `customer` on rework spend bills the
 * founder for the crew's own mistake, which is precisely what D99 forbids.
 */
export const CostAttribution = z.enum([
    "customer",
    "platform_rework",
    "estimate_overrun",
    "safety_absorbed",
    "acquisition",
]);
/**
 * The agent lane the spend happened on, as named by `crew/lanes/<name>.md`. REQUIRED on
 * `model_tokens` spend: 0021's `fix_cost_events_model_spend_names_a_lane` CHECK (0021:122) refuses a
 * model row with a null lane.
 *
 * Validated as a slug rather than as a closed set: the 54 lane names live in `crew/lanes/` and a
 * hard-coded enum here would drift from them silently.
 */
export const LaneName = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "a lane name is a lower-case slug, as in crew/lanes/")
    .brand();
/**
 * The vendor billed, as keyed in `crew/config/model-roster.json` (`anthropic`, `openai`, `google`,
 * and the non-model vendors). A slug, not a closed enum, for the same anti-drift reason as
 * `LaneName`.
 */
export const ProviderName = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "a provider name is a lower-case slug")
    .brand();
/**
 * A quantity as an EXACT DECIMAL STRING, never a float. `fix_cost_events.quantity` is
 * `numeric(20,6)` and the row carries a `quantity * rate = amount` CHECK (0021:118); a JS float
 * cannot represent every value of that column, and drizzle maps numeric to string for exactly this
 * reason (packages/db/src/schema/fix-cost-events.ts:20-24). A leading `-` is legal: a correction is
 * a compensating row with a negative quantity (0021:144, fix-cost-events.ts:103).
 */
export const CostQuantity = z
    .string()
    .regex(/^-?(?:0|[1-9]\d{0,13})(?:\.\d{1,6})?$/, "a quantity is an exact decimal, at most 6 places")
    .brand();
//# sourceMappingURL=fix-cost-events.js.map